import axios from "axios";
import { getUserFromLS } from "../utils/getUserFromLS";
import { SOCIAL_CONTENT_TYPE } from "../Types/account";

/** Payload sent for each cadence step (matches backend `CadenceStep`). */
export interface CadenceStepPayload {
    content_type: SOCIAL_CONTENT_TYPE;
    objective?: string | null;
    /** Optional per-step voice override (backend-supported). */
    voice_key?: string | null;
}

export const MAX_CADENCE_STEPS = 5;

/** Channels that must be the only step in a cadence (no mixing with other channels). */
export const SOLO_CADENCE_CONTENT_TYPES = new Set<SOCIAL_CONTENT_TYPE>([
    SOCIAL_CONTENT_TYPE.LINKEDIN_POST,
    SOCIAL_CONTENT_TYPE.BLOG,
]);

export function isSoloCadenceContentType(contentType: SOCIAL_CONTENT_TYPE): boolean {
    return SOLO_CADENCE_CONTENT_TYPES.has(contentType);
}

export function createCadenceStep(
    contentType: SOCIAL_CONTENT_TYPE,
    objective?: string | null,
    voice_key?: string | null
): CadenceStepPayload {
    const trimmed = objective?.trim();
    const out: CadenceStepPayload = {
        content_type: contentType,
        objective: trimmed ? trimmed : null,
    };
    if (voice_key !== undefined) {
        out.voice_key = voice_key?.trim() ? voice_key : null;
    }
    return out;
}

/** Single-action generators (calendar, thought leadership) send a one-element plan. */
export function createSingleStepPlan(
    contentType: SOCIAL_CONTENT_TYPE,
    objective: string | null = null
): CadenceStepPayload[] {
    return [createCadenceStep(contentType, objective)];
}

export interface IAccountInsightSPRequest {
    title?: string | null;
    voice_key?: string | null;
    context?: string | null;
    content_plan: CadenceStepPayload[];
    /** When false, suppresses user notification for this generation (backend default is true). */
    notify?: boolean | null;
    contact_id?: number | null;
    target_type: "ACCOUNT_INSIGHT";
    account_insight_id: number;
}

export interface IIndustryInsightSPRequest {
    title?: string | null;
    voice_key?: string | null;
    context?: string | null;
    content_plan: CadenceStepPayload[];
    notify?: boolean | null;
    contact_id?: number | null;
    target_type: "INDUSTRY_INSIGHT";
    industry_insight_id: number;
    account_id?: string | null;
}

export interface IContactIntelligenceSPRequest {
    title?: string | null;
    voice_key?: string | null;
    context?: string | null;
    content_plan: CadenceStepPayload[];
    notify?: boolean | null;
    target_type: "CONTACT_INTELLIGENCE";
    contact_id: number;
}

export interface IAssetSPRequest {
    title?: string | null;
    voice_key?: string | null;
    context?: string | null;
    content_plan: CadenceStepPayload[];
    notify?: boolean | null;
    target_type: "ASSET_INSIGHT";
    asset_id: number;
}

export interface IInsightsItemSPRequest extends Omit<IIndustryInsightSPRequest, "target_type" | "industry_insight_id"> {
    target_type: "INSIGHTS_ITEM";
    insight_item_id: string;
}

export interface IRequestSocialPromptOptions {
    /**
     * Optional `user_id` query parameter. Only honoured by the backend when the
     * request is authenticated with a service-account API key — bearer-token
     * auth always uses the authenticated user. Safe to omit.
     */
    userIdOverride?: string | null;
}

export const requestSocialPrompt = (
    data: IInsightsItemSPRequest | IAccountInsightSPRequest | IIndustryInsightSPRequest | IContactIntelligenceSPRequest | IAssetSPRequest,
    callback: (response: any) => void,
    errorCallback: (error: any) => void,
    options: IRequestSocialPromptOptions = {}
) => {
    const user = getUserFromLS();
    const baseUrl = `${import.meta.env.VITE_API_BASE_URL}/social_prompts/generate`;
    const URL = options.userIdOverride
        ? `${baseUrl}?user_id=${encodeURIComponent(options.userIdOverride)}`
        : baseUrl;

    return axios(URL, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${user.access_token}`,
            "Content-Type": "application/json",
        },
        data: data,
    })
        .then((response) => {
            if (callback != null) {
                callback(response.data);
            }
        })
        .catch((error) => {
            if (errorCallback != null) {
                errorCallback(error);
            }
        });
};
